import { useState, useEffect } from 'react';
import { knowledgeBaseAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  BookOpen, Plus, Trash2, Loader2, FileText, HelpCircle, Globe,
  Search, CheckCircle2, Brain, Clock
} from 'lucide-react';
import { toast } from 'sonner';

export default function KnowledgeBasePage() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchMethod, setSearchMethod] = useState('');
  const [form, setForm] = useState({ title: '', content: '', doc_type: 'faq' });

  useEffect(() => {
    knowledgeBaseAPI.list()
      .then(res => setDocs(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const loadDocs = async () => {
    const res = await knowledgeBaseAPI.list();
    setDocs(res.data);
  };

  const handleCreate = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    setSaving(true);
    try {
      await knowledgeBaseAPI.create(form);
      toast.success('Document added');
      setShowCreate(false);
      setForm({ title: '', content: '', doc_type: 'faq' });
      loadDocs();
    } catch (err) {
      toast.error('Failed to add document');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await knowledgeBaseAPI.delete(id);
      toast.success('Document deleted');
      loadDocs();
      setSearchResults(null);
    } catch { toast.error('Failed to delete'); }
  };

  const handleSearch = async () => {
    if (!search.trim()) { setSearchResults(null); return; }
    setSearching(true);
    try {
      const res = await knowledgeBaseAPI.search(search);
      setSearchResults(res.data.results);
      setSearchMethod(res.data.method);
    } catch { toast.error('Search failed'); }
    setSearching(false);
  };

  const getTypeIcon = (type) => {
    const icons = { faq: HelpCircle, document: FileText, url: Globe };
    return icons[type] || FileText;
  };

  const filtered = docs.filter(d =>
    d.title?.toLowerCase().includes(search.toLowerCase()) ||
    d.content?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-amber-500 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6 max-w-7xl" data-testid="knowledge-base-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit'] text-white">Knowledge Base</h1>
          <p className="text-slate-400 mt-1">Train your AI agents with company knowledge</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="add-document-button"
          className="bg-amber-600 hover:bg-amber-700 text-white shadow-[0_0_20px_rgba(245,158,11,0.3)]">
          <Plus className="w-4 h-4 mr-2" /> Add Document
        </Button>
      </div>

      {/* Search */}
      <div className="flex gap-3 items-center">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); if (!e.target.value) setSearchResults(null); }}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Semantic search..."
            data-testid="kb-search-input"
            className="pl-10 bg-slate-950 border-white/10 text-white placeholder:text-slate-600"
          />
        </div>
        <Button onClick={handleSearch} disabled={searching || !search.trim()} data-testid="kb-search-btn"
          className="bg-amber-600 hover:bg-amber-700 text-white h-10 px-4">
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />}
          <span className="ml-2 hidden sm:inline">RAG Search</span>
        </Button>
      </div>

      {/* Search Results */}
      {searchResults && (
        <div className="space-y-3" data-testid="kb-search-results">
          <div className="flex items-center gap-2">
            <Brain className="w-4 h-4 text-amber-400" />
            <span className="text-sm text-slate-300">
              {searchResults.length} results via <span className="text-amber-400 font-medium">{searchMethod === 'semantic' ? 'Semantic Search (RAG)' : 'Text Match'}</span>
            </span>
            <button onClick={() => setSearchResults(null)} className="text-xs text-slate-500 hover:text-white ml-auto">Clear</button>
          </div>
          {searchResults.map((doc, i) => {
            const TypeIcon = getTypeIcon(doc.doc_type);
            return (
              <div key={doc.id} className="glass-card rounded-xl p-4 border border-amber-500/20 animate-fade-in" style={{ animationDelay: `${i * 60}ms` }}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <TypeIcon className="w-4 h-4 text-amber-400" />
                    <span className="text-white font-medium text-sm">{doc.title}</span>
                    <Badge className="bg-amber-600/20 text-amber-300 border-amber-600/30 text-xs">{doc.doc_type}</Badge>
                  </div>
                  {doc.relevance_score !== undefined && (
                    <span className="text-xs text-amber-400 font-mono">{(doc.relevance_score * 100).toFixed(1)}% match</span>
                  )}
                </div>
                <p className="text-sm text-slate-400 line-clamp-2">{doc.content}</p>
              </div>
            );
          })}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="glass-card rounded-xl p-12 text-center" data-testid="no-docs">
          <BookOpen className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold font-['Outfit'] text-white mb-2">No documents yet</h3>
          <p className="text-slate-400 mb-6">Add FAQs, documents, or URLs to train your AI agents</p>
          <Button onClick={() => setShowCreate(true)} className="bg-amber-600 hover:bg-amber-700 text-white">
            <Plus className="w-4 h-4 mr-2" /> Add Document
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((doc, i) => {
            const TypeIcon = getTypeIcon(doc.doc_type);
            return (
              <div key={doc.id} className="glass-card rounded-xl p-5 animate-fade-in" style={{ animationDelay: `${i * 60}ms` }}
                data-testid={`kb-doc-${doc.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-emerald-600/20 flex items-center justify-center">
                      <TypeIcon className="w-4 h-4 text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-medium text-sm">{doc.title}</h3>
                      <span className="text-xs text-slate-500 capitalize">{doc.doc_type}</span>
                    </div>
                  </div>
                  <Badge className={`text-xs ${doc.status === 'indexed'
                    ? 'bg-emerald-600/20 text-emerald-400 border-emerald-600/30'
                    : 'bg-amber-600/20 text-amber-400 border-amber-600/30'}`}>
                    {doc.status === 'indexed'
                      ? <><CheckCircle2 className="w-3 h-3 mr-1" /> Indexed</>
                      : <><Clock className="w-3 h-3 mr-1" /> Embedding...</>}
                  </Badge>
                </div>
                <p className="text-sm text-slate-400 line-clamp-3 mb-4">{doc.content}</p>
                <div className="flex items-center justify-between pt-3 border-t border-white/5">
                  <span className="text-xs text-slate-500">{new Date(doc.created_at).toLocaleDateString()}</span>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(doc.id)}
                    className="text-slate-400 hover:text-red-400 hover:bg-red-500/10 text-xs"
                    data-testid={`delete-doc-${doc.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg bg-[#0B1221] border-white/10 text-white" data-testid="kb-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Outfit']">Add Knowledge</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-300 text-sm mb-2 block">Type</Label>
              <Select value={form.doc_type} onValueChange={(v) => setForm(p => ({ ...p, doc_type: v }))}>
                <SelectTrigger className="bg-slate-950 border-white/10 text-white" data-testid="kb-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10">
                  <SelectItem value="faq" className="text-white hover:bg-white/5">FAQ</SelectItem>
                  <SelectItem value="document" className="text-white hover:bg-white/5">Document</SelectItem>
                  <SelectItem value="url" className="text-white hover:bg-white/5">URL</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300 text-sm mb-2 block">Title *</Label>
              <Input value={form.title} onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g., Return Policy FAQ"
                data-testid="kb-title-input" className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600" />
            </div>
            <div>
              <Label className="text-slate-300 text-sm mb-2 block">Content *</Label>
              <Textarea value={form.content} onChange={(e) => setForm(p => ({ ...p, content: e.target.value }))}
                placeholder="Enter the knowledge content..." rows={6}
                data-testid="kb-content-input" className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600" />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="ghost" onClick={() => setShowCreate(false)} className="text-slate-400 hover:text-white">Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="save-document-button"
              className="bg-amber-600 hover:bg-amber-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Add Document
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
